/**
 * Circular-safe JSON serializer for the in-app console capture pipeline.
 *
 * Guarantees:
 *   - Never throws on circular references (WeakSet guard marks them "[Circular]")
 *   - Truncates objects nested beyond SERIALIZE_DEPTH with "…"
 *   - Truncates the final JSON string if it exceeds PAYLOAD_MAX_BYTES
 *   - Serializes Error objects to { name, message, stack }
 *
 * Constants exported for tests and dependent modules:
 *   SERIALIZE_DEPTH     — maximum nesting depth before truncation (4)
 *   PAYLOAD_MAX_BYTES   — maximum byte length of serialized output (8192)
 */

/** Maximum nesting depth before child values are replaced with "…". */
export const SERIALIZE_DEPTH = 4;

/** Maximum byte length of the serialized string (UTF-8 code units ≈ chars for ASCII). */
export const PAYLOAD_MAX_BYTES = 8192;

/** Marker inserted when a circular reference is detected. */
const CIRCULAR_MARKER = '[Circular]';

/** Marker appended when output is truncated. */
const TRUNCATION_MARKER = '…';

/**
 * Recursively sanitizes a value for safe JSON serialization.
 *
 * @param value  - any JavaScript value
 * @param seen   - WeakSet tracking objects currently on the traversal stack
 * @param depth  - current nesting depth (starts at 0 at the root)
 */
function sanitize(
  value: unknown,
  seen: WeakSet<object>,
  depth: number,
): unknown {
  // Primitives: pass through as-is
  if (value === null || typeof value !== 'object' && typeof value !== 'function') {
    return value;
  }

  // Functions: not JSON-serializable, replace with a label
  if (typeof value === 'function') {
    return '[Function]';
  }

  // Error objects: extract the meaningful fields explicitly because JSON.stringify
  // produces "{}" for Error instances (enumerable properties are none by default).
  if (value instanceof Error) {
    // Avoid treating Error as a circular-ref object; snapshot its fields.
    const errorSnapshot: Record<string, unknown> = {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
    // Recurse into errorSnapshot (depth unchanged — Error is treated as 1 level)
    return sanitize(errorSnapshot, seen, depth);
  }

  // Circular reference guard
  if (seen.has(value as object)) {
    return CIRCULAR_MARKER;
  }

  // Depth cap: replace too-deep values with the truncation marker
  if (depth >= SERIALIZE_DEPTH) {
    return TRUNCATION_MARKER;
  }

  // Add to seen BEFORE recursing; do NOT delete after — this ensures any
  // repeated reference (including diamond-shaped graphs) is detected and
  // marked "[Circular]" rather than serialized twice, which could risk
  // memory blowup or misleading duplicate output in the console overlay.
  seen.add(value as object);

  if (Array.isArray(value)) {
    return (value as unknown[]).map(item => sanitize(item, seen, depth + 1));
  }

  const obj: Record<string, unknown> = {};
  for (const key of Object.keys(value as object)) {
    obj[key] = sanitize(
      (value as Record<string, unknown>)[key],
      seen,
      depth + 1,
    );
  }
  return obj;
}

/**
 * Serializes any JavaScript value to a JSON string that is safe to store in
 * the ring buffer.
 *
 * - Circular references are replaced with `"[Circular]"`.
 * - Values at depth > SERIALIZE_DEPTH are replaced with `"…"`.
 * - If the resulting string exceeds PAYLOAD_MAX_BYTES, it is sliced and the
 *   truncation marker `…` is appended.
 */
export function safeSerialize(value: unknown): string {
  const seen = new WeakSet<object>();
  const sanitized = sanitize(value, seen, 0);

  let json: string;
  try {
    json = JSON.stringify(sanitized);
  } catch {
    // Should never reach here after sanitization, but guard just in case.
    json = JSON.stringify(String(value));
  }

  if (json.length > PAYLOAD_MAX_BYTES) {
    // Slice to budget minus 1 char to fit the truncation marker.
    json = json.slice(0, PAYLOAD_MAX_BYTES - 1) + TRUNCATION_MARKER;
  }

  return json;
}
